import { Paths } from "@app/routes";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  cn,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Item,
  Label,
  ScrollArea,
  WithToolTip,
} from "@atlas/core";
import { useSessionData, useTranslation } from "@atlas/sdk";
import { CheckoutCatalogs } from "@modules/catalog/pages/checkout/CheckoutCatalogs";
import { PackageSummaryCard } from "@modules/catalog/pages/checkout/PackageSummaryCard";
import { useHubCurrentCart, useHubPackage } from "@query";
import { useCreatePackageSave, useEditPackageSave } from "@shared/hooks";
import { plainCatalogDescription } from "@utils";
import { Expand, Info, ShoppingCart, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { EditPackageDialog } from "../../modules/package-manager/pages/EditPackageDialog";
import { EditablePackageVisibility } from "./EditablePackageVisibility";
import { PackageActionsBar } from "./PackageActionsBar";
import { PackageSwitcher } from "./PackageSwitcher";

interface IHubCartProps {
  packageCartCount: number;
  headerCartBump: boolean;
  onAnimationEnd: () => void;
}

export function HubCartButton({
  packageCartCount,
  headerCartBump,
  onAnimationEnd,
  isOpen,
  onToggle,
}: IHubCartProps & { isOpen: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const isCartPage =
    location.pathname.includes(`/${Paths.PackageManager}`) &&
    !params.has("view") &&
    !params.has("tab") &&
    !params.has("deploymentId") &&
    (!params.has("packageId") || params.get("fromTab") === "packages");

  const cartTriggerLabels = useMemo(
    () => ({
      title: t("hub.header.catalog_cart_title", {
        count: packageCartCount,
        defaultValue: "Package ({{count}} items)",
      }),
      aria: t("hub.header.catalog_cart_aria", {
        count: packageCartCount,
        defaultValue: "Package, {{count}} items",
      }),
    }),
    [packageCartCount, t],
  );

  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleCartClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.detail === 1) {
        const timeout = setTimeout(() => {
          onToggle();
        }, 200);
        setClickTimeout(timeout);
      } else if (e.detail === 2) {
        if (clickTimeout) clearTimeout(clickTimeout);
        navigate(`/${Paths.PackageManager}`);
      }
    },
    [clickTimeout, onToggle, navigate],
  );

  if (isCartPage) {
    return null;
  }

  return (
    <div
      className={cn(
        "relative inline-flex shrink-0",
        headerCartBump && "animate-bounce duration-100 repeat-2",
      )}
      onAnimationEnd={onAnimationEnd}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={"relative shrink-0 "}
        title={cartTriggerLabels.title}
        aria-label={cartTriggerLabels.aria}
        aria-expanded={isOpen}
        onClick={handleCartClick}>
        <ShoppingCart className="size-4" aria-hidden />
      </Button>

      {packageCartCount > 0 ? (
        <Badge
          variant="primary"
          size="sm"
          shape="circle"
          className="pointer-events-none absolute -top-1 right-0 translate-x-1.5">
          {packageCartCount > 99 ? "99+" : packageCartCount}
        </Badge>
      ) : null}
    </div>
  );
}

export function HubCartPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSwitchingPackage, setIsSwitchingPackage] = useState(false);

  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0,
  });
  const { data: cartPackage } = useHubPackage(userHubCart?.packageId ?? 0, {
    enabled: userHubCart?.packageId != null && userHubCart?.packageId > 0,
  });

  const catalogRelations = useMemo(
    () => cartPackage?.catalogRelations ?? [],
    [cartPackage],
  );

  const [editPackageOpen, setEditPackageOpen] = useState(false);
  const [createPackageOpen, setCreatePackageOpen] = useState(false);

  const { handleEditPackageSave, isEditPackagePending } = useEditPackageSave(
    cartPackage,
    setEditPackageOpen,
  );

  const { handleCreatePackageSave, isCreatePending } =
    useCreatePackageSave(setCreatePackageOpen);

  const openFullCart = useCallback(() => {
    onClose();
    navigate(`/${Paths.PackageManager}`);
  }, [onClose, navigate]);

  const packageSummary = useMemo(() => {
    const title =
      (cartPackage?.label ?? cartPackage?.name ?? "").trim() ||
      t("hub.header.cart_package_fallback", {
        defaultValue: "Package",
      });
    const blurb =
      plainCatalogDescription(cartPackage?.description ?? "").trim() || null;
    return { title, blurb };
  }, [cartPackage, t]);

  const expandARIALabel = useMemo(
    () =>
      t("hub.header.cart_expand", {
        defaultValue: "Open full package",
      }),
    [t],
  );

  const closeARIALabel = useMemo(
    () =>
      t("hub.header.cart_close", {
        defaultValue: "Close",
      }),
    [t],
  );

  return (
    <div className="flex h-full w-full min-h-0 flex-col overflow-hidden border-l border-border bg-background">
      <div className="flex h-full w-full min-h-0 flex-col p-4 gap-4">
        <div className="flex shrink-0 flex-col gap-1">
          <div className="flex items-center justify-between">
            <Label className="text-lg font-semibold">
              {t("hub.header.package_details", {
                defaultValue: "Package Details",
              })}
            </Label>
            <div className="flex shrink-0 gap-2">
              <WithToolTip tooltipText={expandARIALabel} className="z-3000">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={expandARIALabel}
                  onClick={openFullCart}>
                  <Expand className="size-4" aria-hidden />
                </Button>
              </WithToolTip>
              <WithToolTip tooltipText={closeARIALabel} className="z-3000">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={closeARIALabel}
                  onClick={onClose}>
                  <X className="size-4" aria-hidden />
                </Button>
              </WithToolTip>
            </div>
          </div>
          <div className="text-left text-sm font-normal text-muted-foreground flex items-center gap-1">
            {!isSwitchingPackage && (
              <>
                <WithToolTip tooltipText={packageSummary.title}>
                  <span className="truncate">
                    {packageSummary.title.length > 20
                      ? `${packageSummary.title.slice(0, 20)}...`
                      : packageSummary.title}
                  </span>
                </WithToolTip>
                <PackageActionsBar
                  cartPackage={cartPackage}
                  itemCount={catalogRelations.length}
                  onEdit={() => setEditPackageOpen(true)}
                  onCreate={() => setCreatePackageOpen(true)}
                  onSwitch={() => setIsSwitchingPackage(true)}
                  buttonClassName="size-8 shrink-0"
                />
              </>
            )}
            {isSwitchingPackage && (
              <div className="flex items-center gap-1 w-full max-w-[250px]">
                <PackageSwitcher
                  currentPackageId={cartPackage?.packageId}
                  onCancel={() => setIsSwitchingPackage(false)}
                  onSuccess={() => setIsSwitchingPackage(false)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex min-h-0 flex-col flex-1 gap-2">
          <Item size="xs" variant="outline">
            <EditablePackageVisibility pkg={cartPackage} />
          </Item>
          {catalogRelations.length === 0 ? (
            <Empty className="mx-auto min-h-0 max-w-3xl flex-1">
              <EmptyMedia>
                <ShoppingCart className="h-6 w-6 text-muted-foreground" />
              </EmptyMedia>
              <EmptyHeader>
                <EmptyTitle>
                  {t("hub.checkout.empty", {
                    defaultValue: "Your package is empty.",
                  })}
                </EmptyTitle>
                <EmptyDescription>
                  {t("hub.checkout.empty_description", {
                    defaultValue:
                      "Explore the catalog to add solutions and add-ons to your package.",
                  })}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              <div className="px-1 pb-2 pt-1 flex flex-col gap-4">
                <Alert variant="info">
                  <Info className="size-4" />
                  <AlertDescription>
                    {t("hub.subscribe_wizard.check_solution_tokens", {
                      defaultValue:
                        "Please ensure the catalogs in your package have valid tokens, otherwise the installation may fail.",
                    })}
                  </AlertDescription>
                </Alert>
                <Label className="text-sm font-medium text-muted-foreground">
                  {t("hub.checkout.added_catalogs_hint", {
                    defaultValue:
                      "Below are the catalogs added to your package:",
                  })}
                </Label>
              </div>
              <ScrollArea className="min-h-0 min-w-50 shrink">
                <div className="flex-col p-0 flex min-h-0">
                  <CheckoutCatalogs
                    formId="hub-cart-form"
                    catalogs={catalogRelations}
                    currentPackage={cartPackage}
                    isPopover
                    onSubmitSuccess={onClose}
                  />
                </div>
              </ScrollArea>
              <div className="w-full pt-4 mt-4 shrink-0">
                <PackageSummaryCard
                  formId="hub-cart-form"
                  isPopover
                  titleLabel={packageSummary.title}
                  itemCount={catalogRelations.length}
                  createdAtIso={cartPackage?.createdByDatetime ?? null}
                  submitDisabled={catalogRelations.length === 0}
                  cartPackage={cartPackage}
                  onCancel={onClose}
                  onEdit={() => setEditPackageOpen(true)}
                  onCreate={() => setCreatePackageOpen(true)}
                  visibility={cartPackage?.visibility}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <EditPackageDialog
        open={editPackageOpen || createPackageOpen}
        variant={createPackageOpen ? "create" : "edit"}
        pkg={createPackageOpen ? null : (cartPackage ?? null)}
        onOpenChange={(open) => {
          if (!open) {
            setEditPackageOpen(false);
            setCreatePackageOpen(false);
          }
        }}
        isSubmitting={
          createPackageOpen ? isCreatePending : isEditPackagePending
        }
        onSave={
          createPackageOpen ? handleCreatePackageSave : handleEditPackageSave
        }
      />
    </div>
  );
}
